import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth as authApi, permissions as permissionsApi, checklist as checklistApi } from "../lib/api";

const AuthContext = createContext(null);

const STORAGE_KEY = "canales.app.v2"; // for non-sensitive UI state (active store/audit, viewAll toggle)

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = not logged, object = logged
  const [activeStore, setActiveStore] = useState(null);
  const [activeAuditId, setActiveAuditId] = useState(null);
  const [viewAllStores, setViewAllStores] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [permissionsMap, setPermissionsMap] = useState(null);

  // Hydrate UI state
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p.activeStore) setActiveStore(p.activeStore);
        if (p.activeAuditId) setActiveAuditId(p.activeAuditId);
        if (typeof p.viewAllStores === "boolean") setViewAllStores(p.viewAllStores);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (user === null) return;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ activeStore, activeAuditId, viewAllStores }),
    );
  }, [activeStore, activeAuditId, viewAllStores, user]);

  // On mount, check session
  useEffect(() => {
    let mounted = true;
    authApi
      .me()
      .then((u) => {
        if (mounted) setUser(u);
      })
      .catch(() => {
        if (mounted) setUser(false);
      })
      .finally(() => {
        if (mounted) setHydrated(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const u = await authApi.login(email, password);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (e) {
      // ignore
    }
    setUser(false);
    setActiveStore(null);
    setActiveAuditId(null);
    setViewAllStores(false);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const u = await authApi.me();
      setUser(u);
      return u;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  const refreshPermissions = useCallback(async () => {
    try {
      const p = await permissionsApi.me();
      setPermissionsMap(p.permissions || {});
      return p.permissions;
    } catch {
      setPermissionsMap({});
      return null;
    }
  }, []);

  // Reload permissions whenever the user changes (login/logout/refresh).
  useEffect(() => {
    if (user && user.id) refreshPermissions();
    else setPermissionsMap(null);
  }, [user, refreshPermissions]);

  // Prefetch the checklist once at login so the runtime i18n lookup
  // (categories + cached translations) is available for every page —
  // dashboard widgets, notifications, findings tables, etc. — without each
  // component having to know about it.
  useEffect(() => {
    if (user && user.id) {
      checklistApi.list().catch(() => { /* non-fatal for i18n prefetch */ });
    }
  }, [user]);

  const can = useCallback(
    (perm) => {
      if (!user) return false;
      if (user.role === "admin") return true;
      if (!permissionsMap) return true; // optimistic until loaded
      return !!permissionsMap[perm];
    },
    [user, permissionsMap],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        logout,
        refreshUser,
        hydrated,
        activeStore,
        setActiveStore,
        activeAuditId,
        setActiveAuditId,
        viewAllStores,
        setViewAllStores,
        permissionsMap,
        refreshPermissions,
        can,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}
